import React, { useEffect } from "react";

import { Filter, LogOut, Plus, Search, Package } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { PRIMARY_COLOR } from "@/constants/constants";
import { Input } from "../ui/input";
import logo from "/src/assets/logo.png";
import { useDispatch, useSelector } from "react-redux";
import { setSearchText } from "@/store/reducers/searchReducer";
import { Button } from "../ui/button";
import { RootState } from "@/store/store";
import { useLocation, useNavigate } from "react-router-dom";
import { openDialog } from "@/store/reducers/dialogReducer";
import {
  FILTER_DIALOG,
  LOGOUT_CONFIRMATION_DIALOG,
  RETURN_CREATION_DIALOG,
  RTO_PACKAGE_ACTIONS_DIALOG,
} from "@/constants/dialog-constants";
import { truncateText } from "@/utils/text-utills";
import { NavigationRoutes } from "../navigation/constants";
import { Roles } from "@/enums";

interface HeaderProps {
  title?: string;
}

const Header: React.FC<HeaderProps> = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.authentication);
  const { filter } = useSelector((state: RootState) => state.filter);
  const [searchText_, setSearchText_] = React.useState("");

  useEffect(() => {
    const searchDynamic = setTimeout(() => {
      dispatch(setSearchText(searchText_.trim()));
    }, 1000);
    return () => clearTimeout(searchDynamic);
  }, [searchText_]);

  const handleLogoClick = () => {
    navigate(NavigationRoutes.DASHBOARD);
  };

  const handleLogout = () => {
    dispatch(openDialog(LOGOUT_CONFIRMATION_DIALOG));
  };

  const handleOpenCreateDialog = () => {
    dispatch(openDialog(RETURN_CREATION_DIALOG));
  };

  const handleOpenFilterDialog = () => {
    dispatch(openDialog(FILTER_DIALOG));
  };

  const handleOpenRtoActionsDialog = () => {
    dispatch(openDialog(RTO_PACKAGE_ACTIONS_DIALOG));
  };

  return (
    <header className="shadow-md">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="hidden md:flex items-center">
            <img
              src={logo}
              alt="Logo"
              className="h-8 w-auto hover:cursor-pointer"
              onClick={handleLogoClick}
            />
          </div>
          <div className="flex flex-1 max-w-xl mx-4 flex-row gap-2">
            <Button
              variant="outline"
              className="h-10 w-10"
              onClick={handleOpenRtoActionsDialog}
            >
              <Package size={20} style={{ color: PRIMARY_COLOR }} />
            </Button>
            <Button
              variant="outline"
              className={`h-10 w-10 bg-white hover:bg-gray-100 ${
                filter.sellerName || filter.statusId
                  ? "border-teal-500 border-2"
                  : ""
              }`}
              onClick={handleOpenFilterDialog}
            >
              <Filter size={20} style={{ color: PRIMARY_COLOR }} />
            </Button>
            <div className="flex flex-row gap-2 flex-grow">
              <div className="relative flex-grow">
                <Input
                  type="number"
                  placeholder="Recherche"
                  className="w-full pl-10 pr-4 h-10"
                  value={searchText_}
                  min={0}
                  onChange={(e) => setSearchText_(e.target.value)}
                />
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
                  strokeWidth={2}
                />
              </div>
            </div>

            {user.roles.includes(Roles.WAREHOUSE_ROLE) &&
              location.pathname === NavigationRoutes.DASHBOARD && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 flex-shrink-0"
                  onClick={handleOpenCreateDialog}
                >
                  <Plus className="h-4 w-4" />
                  <span className="sr-only">Add new item</span>
                </Button>
              )}
          </div>
          <div className="flex items-center gap-2">
            <p
              className="text-sm font-semibold hidden md:block"
              style={{ color: PRIMARY_COLOR }}
            >
              {truncateText(user.email, 20)}
            </p>
            <Separator
              orientation="vertical"
              className="h-6 w-0.5 mx-2 hidden md:block"
              style={{ backgroundColor: PRIMARY_COLOR }}
            />
            <Button
              variant="outline"
              className="p-3 bg-white hover:bg-gray-100"
              onClick={handleLogout}
            >
              <LogOut size={20} style={{ color: PRIMARY_COLOR }} />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
